/**
 * Recipes page: flavor tabs + recipe dialogs.
 * #<flavor> selects a flavor tab; #<recipe-id> selects its flavor and opens the recipe.
 */
class SrslyRecipeBrowser extends HTMLElement {
  connectedCallback() {
    this.tabs = /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
    this.panels = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tabpanel"]')]);

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    window.addEventListener('hashchange', this.#fromHash);

    for (const dialog of this.querySelectorAll('dialog')) {
      // Close when clicking the backdrop.
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', () => document.documentElement.classList.remove('srsly-dialog-open'));
    }

    this.#fromHash();
    // Scroll again once images have loaded and the page has settled.
    if (location.hash && document.readyState !== 'complete') {
      window.addEventListener('load', () => this.#scrollToSelf(), { once: true });
    }
  }

  #scrollToSelf() {
    // The theme may scroll an inner container rather than the window, so let the browser find it.
    requestAnimationFrame(() => this.scrollIntoView({ block: 'start', behavior: 'auto' }));
  }

  disconnectedCallback() {
    window.removeEventListener('hashchange', this.#fromHash);
  }

  /** @param {string} slug @param {{ focus?: boolean, updateHash?: boolean }} [options] */
  select(slug, { focus = false, updateHash = true } = {}) {
    const tab = this.tabs.find((t) => t.dataset.flavor === slug);
    if (!tab) return;

    for (const t of this.tabs) {
      const active = t === tab;
      t.setAttribute('aria-selected', String(active));
      t.tabIndex = active ? 0 : -1;
    }
    for (const panel of this.panels) panel.hidden = panel.dataset.flavor !== slug;

    // Center the chosen tab in the (horizontally scrolling on phones) tab row without moving the page.
    const strip = /** @type {HTMLElement | null} */ (tab.parentElement);
    if (strip && strip.scrollWidth > strip.clientWidth) {
      strip.scrollTo({ left: tab.offsetLeft - (strip.clientWidth - tab.offsetWidth) / 2, behavior: 'smooth' });
    }
    if (focus) tab.focus();
    if (updateHash) history.replaceState(null, '', `#${slug}`);
  }

  /** @param {string} id */
  open(id) {
    this.closeAll();
    const dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector(`[data-recipe-dialog="${CSS.escape(id)}"]`));
    if (!dialog) return;
    dialog.showModal();
    document.documentElement.classList.add('srsly-dialog-open');
  }

  closeAll() {
    for (const dialog of /** @type {NodeListOf<HTMLDialogElement>} */ (this.querySelectorAll('dialog[open]'))) {
      dialog.close();
    }
  }

  #fromHash = () => {
    const id = decodeURIComponent(location.hash.slice(1));
    if (!id) return;
    this.closeAll();
    if (this.tabs.some((t) => t.dataset.flavor === id)) {
      this.select(id, { updateHash: false });
      this.#scrollToSelf();
      return;
    }
    const recipe = /** @type {HTMLElement | null} */ (this.querySelector(`#${CSS.escape(id)}`));
    const panel = /** @type {HTMLElement | null} */ (recipe?.closest('[role="tabpanel"]'));
    if (recipe && panel?.dataset.flavor) {
      this.select(panel.dataset.flavor, { updateHash: false });
      this.#scrollToSelf();
      this.open(id);
    }
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const tab = /** @type {HTMLElement | null} */ (target.closest('[role="tab"]'));
    if (tab?.dataset.flavor) {
      this.select(tab.dataset.flavor);
      return;
    }
    const opener = /** @type {HTMLElement | null} */ (target.closest('[data-open-recipe]'));
    if (opener?.dataset.openRecipe) {
      this.open(opener.dataset.openRecipe);
      return;
    }
    if (target.closest('[data-close-recipe]')) {
      /** @type {HTMLDialogElement | null} */ (target.closest('dialog'))?.close();
    }
  };

  /** Arrow keys move between flavor tabs. @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const tab = /** @type {HTMLElement} */ (event.target).closest('[role="tab"]');
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const index = this.tabs.indexOf(/** @type {HTMLButtonElement} */ (tab));
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = this.tabs.length - 1;
    this.select(this.tabs[next].dataset.flavor || '', { focus: true });
  };
}

if (!customElements.get('srsly-recipe-browser')) {
  customElements.define('srsly-recipe-browser', SrslyRecipeBrowser);
}
